import { safeParseJson } from '../../utils/safeParseJson';

export const GRADING_SESSION_SNAPSHOT_KEY = 'dg_grading_session_snapshot_v1';

export type GradingSessionProgress = {
  graded: number;
  total: number;
  draftCount: number;
  finalCount: number;
};

export type GradingSessionSnapshot = {
  courseId: string;
  assignmentId: string;
  focusIndex: number | null;
  draftScore: string;
  draftFeedback: string;
  rubricText?: string;
  savedAt: number;
  courseName?: string;
  assignmentTitle?: string;
  studentId?: string;
  studentName?: string;
  viewerPageIndex?: number;
  viewerZoomPercent?: number;
  viewerFitWidth?: boolean;
  progress?: GradingSessionProgress;
};

function isValidSnapshot(raw: unknown): raw is GradingSessionSnapshot {
  if (!raw || typeof raw !== 'object') return false;
  const o = raw as Record<string, unknown>;
  return (
    typeof o.courseId === 'string' &&
    o.courseId.length > 0 &&
    typeof o.assignmentId === 'string' &&
    o.assignmentId.length > 0 &&
    (o.focusIndex === null || typeof o.focusIndex === 'number') &&
    typeof o.draftScore === 'string' &&
    typeof o.draftFeedback === 'string' &&
    typeof o.savedAt === 'number'
  );
}

export function loadGradingSessionSnapshot(): GradingSessionSnapshot | null {
  const parsed = safeParseJson<unknown>(
    typeof window !== 'undefined' ? localStorage.getItem(GRADING_SESSION_SNAPSHOT_KEY) : null,
    null
  );
  return isValidSnapshot(parsed) ? parsed : null;
}

export function saveGradingSessionSnapshot(input: Omit<GradingSessionSnapshot, 'savedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: GradingSessionSnapshot = { ...input, savedAt: Date.now() };
    localStorage.setItem(GRADING_SESSION_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode
  }
}

export function clearGradingSessionSnapshot(): void {
  try {
    localStorage.removeItem(GRADING_SESSION_SNAPSHOT_KEY);
  } catch {
    // ignore
  }
}

export function snapshotMatchesAssignment(
  snapshot: GradingSessionSnapshot | null,
  courseId: string | undefined,
  assignmentId: string | undefined
): snapshot is GradingSessionSnapshot {
  if (!snapshot || !courseId || !assignmentId) return false;
  return snapshot.courseId === courseId && snapshot.assignmentId === assignmentId;
}
